import { useApi } from 'contexts/Api/Api';
import { useContent } from 'contexts/Content/Content';
import {
    getPathname,
    getPlinkromaticRelativePath,
    getUrl,
    isPlinkromaticApprovedLessonHeaderPath,
    resolveKromaticSiteUrl,
    withUtm,
} from 'helpers';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Returns current origin after mount so in-app links stay on staging when on staging. SSR/first paint use relative path to avoid hydration mismatch. */
function useRuntimeOrigin(): string | undefined {
    const [origin, setOrigin] = useState<string | undefined>(undefined);
    useEffect(() => {
        setOrigin(window.location.origin);
    }, []);
    return origin;
}
import {
    Container,
    Content,
    Logo,
    LogoMobile,
    LogoPicture,
    LogoWrap,
    PortionMarking,
    Tagline,
    Text,
    Title,
    UserActionLink,
    UserActions,
    UserContainer,
    UserImage,
    UserName,
    UserPicture,
    UserProfile,
    UserProfileMenu,
    UserProfileMenuButton,
    UserProfileMenuLink,
    Wrap,
} from './Header.styled';

export type HeaderAuthState =
    | 'landing_guest'
    | 'landing_member'
    | 'public_lesson_guest'
    | 'public_lesson_member'
    | 'game_member';

export function getHeaderAuthState(path: string, authEnabled: boolean, authenticated: boolean): HeaderAuthState | null {
    if (!authEnabled) return null;
    if (path === '/') return authenticated ? 'landing_member' : 'landing_guest';
    if (isPlinkromaticApprovedLessonHeaderPath(path)) {
        return authenticated ? 'public_lesson_member' : 'public_lesson_guest';
    }
    if (path.startsWith('/lessons/') && authenticated) return 'game_member';
    return null;
}

export default function Header({ children, style, visible = true }: HeaderProps) {
    const [openProfileMenu, setOpenProfileMenu] = useState(false);
    const runtimeOrigin = useRuntimeOrigin();
    const api = useApi();
    const content = useContent();
    const router = useRouter();
    const userContainerRef = useRef<HTMLDivElement>(null);

    const { background, backgroundMobile, foreground, logo, logoLink, logoMobile, tagline, textOffset, title } =
        content.header;

    const appHref = useCallback(
        (path: string) => (runtimeOrigin ? `${runtimeOrigin}${getUrl(path)}` : getUrl(path)),
        [runtimeOrigin]
    );
    /** Links to site root (e.g. /mykit, /user) — no app base path. SSR uses relative path so href resolves to current host. */
    const siteRootHref = useCallback(
        (path: string) => (runtimeOrigin ? `${runtimeOrigin}${path}` : path),
        [runtimeOrigin]
    );
    /**
     * Logo link target. When `logoLink` points to a kromatic.com property it
     * is an outbound link, so it carries UTM attribution per the workspace
     * UTM convention (#444). Internal fallbacks (e.g. "/") are left untouched
     * by `withUtm` (non-kromatic / relative URLs pass through unchanged).
     */
    const logoHref = useMemo(() => {
        if (!logoLink) return '/';
        return withUtm(resolveKromaticSiteUrl(logoLink), { medium: 'nav', campaign: 'brand-home' });
    }, [logoLink]);
    const { portionMarking } = content.general;

    const headerAuthState = useMemo(() => {
        const pathname = getPathname(router.asPath);
        const path = getPlinkromaticRelativePath(pathname);
        return getHeaderAuthState(path, api.authEnabled, api.isAuthenticated);
    }, [api.authEnabled, api.isAuthenticated, router.asPath]);

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (openProfileMenu && userContainerRef.current && !userContainerRef.current.contains(e.target as Node)) {
                setOpenProfileMenu(false);
            }
        },
        [openProfileMenu]
    );

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            setOpenProfileMenu(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('click', handleClick);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('click', handleClick);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClick, handleKeyDown]);

    function handleLogout() {
        void api.logout();
        setOpenProfileMenu(false);
    }

    return (
        <Wrap style={style}>
            {portionMarking && <PortionMarking data-testid="header-portion-marking">{portionMarking}</PortionMarking>}
            {visible && (
                <Container $background={background} $backgroundMobile={backgroundMobile || background}>
                    <LogoWrap>
                        <a aria-label="Plinkromatic home" data-testid="header-logo-link" href={logoHref}>
                            <LogoPicture>
                                <LogoMobile alt="Logo" src={getUrl(logoMobile || logo)} />
                                <Logo alt="Logo" src={getUrl(logo)} />
                            </LogoPicture>
                        </a>
                    </LogoWrap>
                    <Content $foreground={foreground}>
                        <Text
                            aria-label={tagline ? `${title}. ${tagline}` : title}
                            data-testid="header-title-link"
                            href={appHref('/')}
                            $foreground={foreground}
                            $offset={textOffset}
                        >
                            <Title>{title}</Title>
                            {tagline && <Tagline>{tagline}</Tagline>}
                        </Text>
                        {children != null && children !== undefined ? children : null}
                        {headerAuthState !== null && (
                            <>
                                {headerAuthState === 'landing_guest' || headerAuthState === 'public_lesson_guest' ? (
                                    <UserActions>
                                        <UserActionLink
                                            aria-label="Log in"
                                            as="button"
                                            data-testid="landing-login-button"
                                            type="button"
                                            onClick={() => api.showAuthModal('login')}
                                        >
                                            Log in
                                        </UserActionLink>
                                        <UserActionLink
                                            aria-label="Join now"
                                            as="button"
                                            data-testid="landing-join-button"
                                            type="button"
                                            onClick={() => api.showAuthModal('join')}
                                        >
                                            Join Now
                                        </UserActionLink>
                                    </UserActions>
                                ) : (
                                    <UserActions>
                                        {headerAuthState === 'landing_member' && (
                                            <UserActionLink
                                                aria-label="My Kit"
                                                data-testid="landing-menu-mykit"
                                                href={siteRootHref('/mykit')}
                                            >
                                                My Kit
                                            </UserActionLink>
                                        )}
                                        <UserContainer ref={userContainerRef}>
                                            <UserProfile
                                                aria-expanded={openProfileMenu}
                                                aria-haspopup="true"
                                                aria-label="Account menu"
                                                data-testid="header-profile-button"
                                                onClick={() => setOpenProfileMenu(!openProfileMenu)}
                                                type="button"
                                            >
                                                <UserName>{api.userInfo?.first_name || 'Account'}</UserName>
                                                <UserPicture>
                                                    <UserImage
                                                        alt="User avatar"
                                                        src={api.userInfo?.avatar || '/assets/user-icon.svg'}
                                                    />
                                                </UserPicture>
                                            </UserProfile>
                                            {openProfileMenu && (
                                                <UserProfileMenu
                                                    aria-label="Account menu"
                                                    data-testid="header-profile-menu"
                                                    role="menu"
                                                >
                                                    <UserProfileMenuLink
                                                        data-testid="header-menu-my-profile"
                                                        href={siteRootHref('/user')}
                                                        role="menuitem"
                                                    >
                                                        My Profile
                                                    </UserProfileMenuLink>
                                                    <UserProfileMenuButton
                                                        data-testid="header-menu-log-out"
                                                        onClick={handleLogout}
                                                        role="menuitem"
                                                        type="button"
                                                    >
                                                        Log Out
                                                    </UserProfileMenuButton>
                                                </UserProfileMenu>
                                            )}
                                        </UserContainer>
                                    </UserActions>
                                )}
                            </>
                        )}
                    </Content>
                </Container>
            )}
        </Wrap>
    );
}
